import { regionLabel } from "../../helpers/listingText";
import type { AiAnswer, AiFilters, AiSuggestion, ChatListing } from "./types";

// Server → UI mappers for the assistant and the official channel
// (backend docs/AI_OFFICIAL_API.md: snake_case `Card`, `AiAnswer`,
// `OfficialMessage`). camelCase aliases are accepted too, so the same card
// renders from the older listing DTOs.

type Raw = Record<string, any>;

const num = (v: unknown): number | null => {
  if (v === null || v === undefined || v === "") return null;
  const n = typeof v === "number" ? v : Number(String(v).replace(",", "."));
  return Number.isFinite(n) && n > 0 ? n : null;
};

const str = (v: unknown): string => (typeof v === "string" ? v : v == null ? "" : String(v));

const CATEGORY: Record<string, string> = {
  apartment: "APARTMENT",
  flat: "APARTMENT",
  house: "HOUSE",
  land: "PLOT",
  plot: "PLOT",
  commercial: "COMMERCIAL",
};

const firstImage = (r: Raw): string | null => {
  const direct = r.photo_url ?? r.photoUrl ?? r.image_url ?? r.imageUrl ?? r.image ?? r.cover_url ?? r.coverUrl;
  if (typeof direct === "string" && direct) return direct;
  const list = r.images ?? r.photos ?? r.image_urls ?? r.imageUrls;
  if (Array.isArray(list) && list.length > 0) {
    const f = list[0];
    if (typeof f === "string") return f;
    if (f && typeof f === "object") return str(f.url ?? f.src) || null;
  }
  return null;
};

export function normalizeListing(input: unknown): ChatListing | null {
  if (!input || typeof input !== "object") return null;
  const r: Raw = (input as Raw).property && typeof (input as Raw).property === "object" ? (input as Raw).property : (input as Raw);
  const id = Number(r.id ?? r.property_id ?? r.listing_id ?? r.listingId);
  if (!Number.isFinite(id) || id <= 0) return null;
  const currencyRaw = str(r.currency ?? r.price_currency ?? r.priceCurrency).toUpperCase();
  const typeRaw = str(r.category ?? r.type ?? r.property_type);
  const type = CATEGORY[typeRaw.toLowerCase()] ?? typeRaw.toUpperCase();
  const short = r.address_short ?? r.addressShort ?? (typeof r.location === "string" ? r.location : null);
  const region = typeof r.region === "string" ? regionLabel(r.region) : "";
  const location =
    str(short) ||
    [region, str(r.district_name ?? r.districtName ?? (typeof r.location === "object" ? r.location?.district : ""))].filter(Boolean).join(", ") ||
    str(typeof r.location === "object" ? r.location?.address : r.address);
  return {
    id,
    title: str(r.title ?? r.name),
    price: num(r.price),
    currency: currencyRaw === "KGS" || currencyRaw === "SOM" ? "KGS" : "USD",
    priceLabel: str(r.price_label ?? r.priceLabel) || null,
    type,
    rooms: num(r.rooms ?? r.rooms_count ?? r.room_count),
    area: num(r.area_m2 ?? r.areaM2 ?? r.area),
    landSotka: num(r.land_sotka ?? r.landSotka) ?? (type === "PLOT" ? num(r.area) : null),
    floor: num(r.floor),
    floors: num(r.floors_total ?? r.floorsTotal ?? r.floors ?? r.total_floors),
    location,
    image: firstImage(r),
    createdAt: str(r.created_at ?? r.createdAt ?? r.createdat) || null,
  };
}

export const normalizeListings = (v: unknown): ChatListing[] =>
  (Array.isArray(v) ? v : []).map(normalizeListing).filter((x): x is ChatListing => x !== null);

export function normalizeSuggestions(data: unknown): AiSuggestion[] {
  const d = data as Raw;
  const list: unknown[] = Array.isArray(data) ? data : Array.isArray(d?.items) ? d.items : Array.isArray(d?.suggestions) ? d.suggestions : [];
  return list
    .map((s, i): AiSuggestion | null => {
      if (typeof s === "string") return s.trim() ? { id: `s${i}`, text: s.trim(), prompt: s.trim() } : null;
      if (!s || typeof s !== "object") return null;
      const r = s as Raw;
      const text = str(r.text ?? r.title ?? r.label).trim();
      if (!text) return null;
      return { id: str(r.id ?? `s${i}`), text, prompt: str(r.prompt ?? r.query).trim() || text, emoji: str(r.emoji) || undefined };
    })
    .filter((x): x is AiSuggestion => x !== null)
    .slice(0, 12);
}

/** In-site path for a turaq.kg link (so it opens with the router); null for other sites. */
export function internalPath(url: string | null | undefined): string | null {
  if (!url) return null;
  try {
    const u = new URL(url, "https://turaq.kg");
    const host = u.hostname.replace(/^www\./, "");
    if (host === "turaq.kg" || u.hostname === window.location.hostname) return `${u.pathname}${u.search}${u.hash}`;
    return null;
  } catch {
    return null;
  }
}

/** Cards shown right away in an answer (2×2 on phones, one row of 4 on desktop). */
export const CARDS_SHOWN = 4;

export const EMPTY_ANSWER: AiAnswer = {
  text: "",
  listings: [],
  total: 0,
  moreUrl: null,
  filters: null,
  suggestSaveSearch: false,
  relaxed: false,
};

/** Merge one server payload (the JSON answer, an SSE `result`/`done`, or a `delta`) into the answer so far. */
export function mergeAnswer(prev: AiAnswer, data: unknown, mode: "replace" | "append" = "replace"): AiAnswer {
  if (typeof data === "string") return { ...prev, text: mode === "append" ? prev.text + data : data };
  if (!data || typeof data !== "object") return prev;
  const r = data as Raw;
  const next: AiAnswer = { ...prev };
  const text = r.reply_text ?? r.replyText ?? r.text;
  if (typeof text === "string") next.text = mode === "append" ? prev.text + text : text;
  if (Array.isArray(r.listings)) next.listings = normalizeListings(r.listings).slice(0, CARDS_SHOWN);
  const total = r.total_count ?? r.totalCount ?? r.total;
  if (typeof total === "number") next.total = total;
  if ("more_url" in r || "moreUrl" in r) next.moreUrl = str(r.more_url ?? r.moreUrl) || null;
  if (r.filters && typeof r.filters === "object") next.filters = r.filters as AiFilters;
  if (typeof r.suggest_save_search === "boolean") next.suggestSaveSearch = r.suggest_save_search;
  if (typeof r.relaxed === "boolean") next.relaxed = r.relaxed;
  return next;
}

const REGION_BY_NAME: Record<string, string> = {
  бишкек: "BISHKEK",
  ош: "OSH",
  чүй: "CHUI",
  чуй: "CHUI",
  "ысык-көл": "YSSYK_KOL",
  "иссык-куль": "YSSYK_KOL",
  нарын: "NARYN",
  талас: "TALAS",
  баткен: "BATKEN",
  "жалал-абад": "JALAL_ABAD",
  "джалал-абад": "JALAL_ABAD",
};

/**
 * /listings URL for the assistant's filters — «Фильтрлерди өзгөртүү» opens
 * the search with them, even when nothing matched (more_url is null then).
 * Same params the server uses for more_url.
 */
export function listingsUrlFromFilters(f: AiFilters | null, query?: string): string {
  const qs = new URLSearchParams();
  if (f) {
    const cat = CATEGORY[str(f.category).toLowerCase()];
    if (cat) qs.set("category", cat);
    const region = str(f.region);
    if (region) qs.set("region", REGION_BY_NAME[region.toLowerCase()] ?? region.toUpperCase());
    if (f.district) qs.set("district", str(f.district));
    if (f.rooms_min) qs.set("rooms", String(f.rooms_min));
    if (f.price_min) qs.set("minPrice", String(f.price_min));
    if (f.price_max) qs.set("maxPrice", String(f.price_max));
    if (str(f.currency).toUpperCase() === "KGS") qs.set("priceCur", "KGS");
  } else if (query) {
    qs.set("q", query);
  }
  const s = qs.toString();
  return `/listings${s ? `?${s}` : ""}`;
}
